<script lang="ts">
  import { ArrowLeft, ArrowRight } from "@poolsideai/web-components/icons";
  import { onMount, type Snippet } from "svelte";
  import type { ClassValue } from "svelte/elements";
  import { fade } from "svelte/transition";

  interface Props {
    wrapperClass?: ClassValue;
    children?: Snippet;
    class?: ClassValue;
  }

  let { wrapperClass, children, class: className, ...rest }: Props = $props();

  let scrollContainer: HTMLDivElement;
  let canScrollLeft = $state(false);
  let canScrollRight = $state(false);

  function updateScrollState(element: HTMLDivElement) {
    const { scrollLeft, scrollWidth, clientWidth } = element;
    canScrollLeft = scrollLeft > 0;
    canScrollRight = scrollLeft < scrollWidth - clientWidth - 1; // -1 for rounding
  }

  function handleScroll(event: Event) {
    const target = event.target as HTMLDivElement;
    updateScrollState(target);
  }

  const SCROLL_AMOUNT = 200;

  function scrollLeft() {
    if (scrollContainer) {
      scrollContainer.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
    }
  }

  function scrollRight() {
    if (scrollContainer) {
      scrollContainer.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
    }
  }

  $effect(() => {
    if (scrollContainer) {
      updateScrollState(scrollContainer);

      // only use ResizeObserver if it's available (not in test environments)
      if (typeof ResizeObserver !== "undefined") {
        const resizeObserver = new ResizeObserver(() => {
          updateScrollState(scrollContainer);
        });
        resizeObserver.observe(scrollContainer);

        return () => {
          resizeObserver.disconnect();
        };
      }
    }
  });

  onMount(() => {
    setTimeout(() => {
      if (!scrollContainer) return;
      updateScrollState(scrollContainer);
    }, 500);
  });
</script>

<div class="table-container relative">
  <div
    bind:this={scrollContainer}
    class={["wrapper -m-px overflow-x-auto p-px", wrapperClass]}
    onscroll={handleScroll}
  >
    <table
      {...rest}
      class={["tb relative w-full border-separate border-spacing-0 rounded-lg text-sm", className]}
    >
      {@render children?.()}
    </table>
  </div>

  <!-- left gradient overlay -->
  {#if canScrollLeft}
    <div
      class="gradient-left pointer-events-none absolute top-0 -left-px h-full w-32 rounded-l-lg bg-gradient-to-r from-mono-000 to-transparent"
      transition:fade={{ duration: 150 }}
    ></div>
    <button
      type="button"
      class="scroll-button absolute top-3 left-3 z-20 cursor-pointer rounded p-0.5 text-foreground-secondary transition-colors hover:bg-alpha-050 hover:text-foreground-primary"
      onclick={scrollLeft}
      aria-label="Scroll left"
      transition:fade={{ duration: 150 }}
    >
      <ArrowLeft class="size-4" />
    </button>
  {/if}

  <!-- right gradient overlay -->
  {#if canScrollRight}
    <div
      class="gradient-right pointer-events-none absolute top-0 right-px h-full w-32 rounded-r-lg bg-gradient-to-l from-mono-000 to-transparent"
      transition:fade={{ duration: 150 }}
    ></div>
    <button
      type="button"
      class="scroll-button absolute top-3 right-4 z-20 cursor-pointer rounded p-0.5 text-foreground-secondary transition-colors hover:bg-alpha-050 hover:text-foreground-primary"
      onclick={scrollRight}
      aria-label="Scroll right"
      transition:fade={{ duration: 150 }}
    >
      <ArrowRight class="h-4 w-4" />
    </button>
  {/if}
</div>

<style>
  @reference "#tailwind.css";
  .table-container {
    @apply w-full;
  }

  .wrapper {
    @apply w-full overscroll-x-none rounded-lg shadow-border-inset;

    /* hide scrollbar - arrow indicators show scroll direction */
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .gradient-left,
  .gradient-right {
    z-index: 10;
    transition: opacity 0.2s ease-in-out;
  }

  .gradient-left,
  .gradient-right {
    /* Additional styling if needed - Tailwind gradients are applied via classes */
  }

  .scroll-button {
    background: transparent;
    border: none;
  }

  .tb {
    table-layout: auto;

    :global(tbody:first-child) {
      :global(tr:first-child) {
        :global(td) {
          border-top: none;
        }
      }
    }

    :global(tr) {
      :global(td:first-child) {
        border-left: none;
      }
    }

    :global(tbody) {
      :global(th) {
        border-top: 1px solid var(--color-alpha-050);
      }

      &:first-child {
        :global(tr:first-child) {
          :global(th) {
            border-top: none;
          }
        }
      }
    }
  }
</style>
